import { Email, HelpOutlineOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, Divider, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Paper, Switch, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";

import { Environment } from "../../../../shared/environment";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { clientService } from "../../../../shared/services/api/client/clientService";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import { globalColorTytle, globalMaskDocument, globalMaskReal } from "../../../../shared/utils";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { LocalAutoCompleteClient } from "./LocalAutoCompleteClient";
import { TenantSolidarityFields } from "./TenantSolidarityFields";
import { useProfile } from "../../../../context/ProfileContext";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { HtmlTooltip } from "../../../../infra/components/tooltipsCustom/tooltipsCuston";

interface TenantDataSectionProps {
	formRef: any;
	isLoading: boolean;
	permissionLevel: number | null;
	Rows: any;
	id: string;
	isSendSerasa: (valor: boolean) => void;
	isTypping: (valor: boolean) => void;
}

const TenantDataSection: React.FC<TenantDataSectionProps> = ({ formRef, isLoading, permissionLevel, Rows, id, isSendSerasa, isTypping }) => {
	const [cpfValue, setCpfValue] = useState("");
	const [enableReason, setEnableReason] = useState(true);
	const [clientId, setClientId] = useState("");
	const [clientType, setClientType] = useState("physical");
	const [sendSerasaChecked, setSendSerasaChecked] = useState(true);
	const [isTippingChecked, setIsTippingChecked] = useState(false);
	const toastHandler = useToast();
	const [isLoadingDocument, setLoadingDocument] = useState(false);
	const { permissions } = useProfile();

	// Solidary Tenant States
	const [hasSolidarity, setHasSolidarity] = useState(false);
	const [solidarityLimit, setSolidarityLimit] = useState(0);
	const [isSolidarityEnabled, setIsSolidarityEnabled] = useState(false);
	const [solidaryTenants, setSolidaryTenants] = useState<any[]>([]);

	// Setup Fee States
	const [isSendClickSetup, setIsSendClickSetup] = useState(false);
	const [setupFeeLimitMin, setSetupFeeLimitMin] = useState(0);
	const [setupFeeLimitMax, setSetupFeeLimitMax] = useState(0);
	const [setupFeeActive, setSetupFeeActive] = useState(false);
	const [showSetupFeeAlert, setShowSetupFeeAlert] = useState(false);
	const handleChangeSerasa = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;
		setSendSerasaChecked(isChecked);
		isSendSerasa(isChecked);
		setEnableReason(isChecked);
	};

	// Alterna estado de tombamento
	const handleTipping = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;
		setIsTippingChecked(isChecked);
		isTypping(isChecked);
	};

	// Alterna estado de Locatário Solidário
	const handleSolidarityToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;
		setIsSolidarityEnabled(isChecked);
		if (!isChecked) {
			setSolidaryTenants([]);
			formRef.current?.setFieldValue("solidaryTenants", []);
		}
	};

	// Alterna estado da Taxa de Setup
	const handleChangeClickSetup = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;
		setIsSendClickSetup(isChecked);
		formRef.current?.setFieldValue("setup_tax", isChecked ? "true" : "false");
		if (!isChecked) {
			formRef.current?.setFieldValue("setup_amount", "0,00");
		}
	};

	const removeMask = (value?: string | null): string => {
		if (!value || typeof value !== "string") return "";
		return value
			.replace(/\D/g, "")
			.replace(/[^0-9]/g, "")
			.replace(/(\d{1,2})$/, ".$1");
	};

	const handleCheckSetupValue = (value: string) => {
		const setupValue = parseFloat(removeMask(value));
		const min = setupFeeLimitMin;
		const max = setupFeeLimitMax;

		if (setupValue < min) {
			formRef.current?.setFieldValue("setup_amount", globalMaskReal(min.toFixed(2)));
			setShowSetupFeeAlert(true);
		} else if (setupValue > max) {
			formRef.current?.setFieldValue("setup_amount", globalMaskReal(max.toFixed(2)));
			setShowSetupFeeAlert(true);
		} else {
			setShowSetupFeeAlert(false);
		}
	};

	// Exibe toast com erro
	const showToastError = (message: string) => {
		toastHandler.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title="Erro" content={message} />,
		});
	};

	const handleAddSolidaryTenant = () => {
		if (solidaryTenants.length < solidarityLimit) {
			setSolidaryTenants([...solidaryTenants, { id: Math.random().toString(36).substr(2, 9) }]);
		} else {
			showToastError(`Limite de ${solidarityLimit} locatários solidários atingido.`);
		}
	};

	const handleRemoveSolidaryTenant = (index: number) => {
		console.log(`Removing solidary tenant at index ${index}`);

		// Explicitly clear the fields for this index in unform before unmounting
		// This ensures that if unform retains the data, it will be empty/null
		// and caught by the filter in sanitizeData
		formRef.current?.setFieldValue(`solidaryTenants[${index}]`, {});
		formRef.current?.setFieldValue(`solidaryTenants[${index}].document`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].name`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].birthDate`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].motherName`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].phone`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].employmentRelationShip`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].sex`, "");
		formRef.current?.setFieldValue(`solidaryTenants[${index}].maritalStatus`, "");

		setTimeout(() => {
			const newTenants = [...solidaryTenants];
			newTenants.splice(index, 1);
			setSolidaryTenants(newTenants);

			// Further cleanup if needed, but the above should handle the data persistence
			if (newTenants.length === 0) {
				formRef.current?.setFieldValue("solidaryTenants", []);
			}
		}, 50);
	};

	// Limpa campos de nome, data de nascimento e nome da mãe
	const clearFields = () => {
		formRef.current?.setFieldValue("name", "");
		formRef.current?.setFieldValue("birthDate", "");
		formRef.current?.setFieldValue("motherName", "");
		formRef.current?.setFieldValue("email", "");
		formRef.current?.setFieldValue("phone", "");
		formRef.current?.setFieldValue("employmentRelationShip", "");
		formRef.current?.setFieldValue("sex", "");
		formRef.current?.setFieldValue("maritalStatus", "");
	};

	// Função para manipular o CPF e fazer validação
	const handleCPF = (value: string) => {
		const documento = value.replace(/\D/g, "").trim();
		setCpfValue(documento);

		if (documento.length < 11) return; // CPF incompleto
		setLoadingDocument(true);
		// Verifica se o documento está na lista de números de teste e se o ambiente é DEV
		if (Environment.AMBIENTE === "DEV" && Array.from({ length: 11 }, (_, index) => `0000000000${index}`).includes(documento)) {
			formRef.current?.setFieldValue("name", `Cliente Teste ${documento.slice(-2)}`);
			formRef.current?.setFieldValue("birthDate", "2000-05-01");
			formRef.current?.setFieldValue("motherName", `Mae do Cliente Teste ${documento.slice(-2)}`);
			formRef.current?.setFieldValue("email", `teste${documento.slice(-2)}@garantti.com`);
			formRef.current?.setFieldValue("phone", `889000000$${documento.slice(-2)}`);
			formRef.current?.setFieldValue("employmentRelationShip", "student");
			formRef.current?.setFieldValue("sex", "male");
			formRef.current?.setFieldValue("maritalStatus", "single");
			setLoadingDocument(false);
			return;
		}

		PeopleService.getPersonDataByDocument(documento)
			.then((data) => {
				if (!data) {
					showToastError(`Documento ${globalMaskDocument(value)} não localizado. Verifique se está correto.`);
					clearFields();
					setLoadingDocument(false);
				} else {
					const { name, birthDate, motherName } = data.naturalPersonData || {};
					formRef.current?.setFieldValue("name", name || "");
					formRef.current?.setFieldValue("birthDate", birthDate || "");
					formRef.current?.setFieldValue("motherName", motherName || "");
					setLoadingDocument(false);
				}
			})
			.catch(() => {
				showToastError(`Erro na pesquisa do documento ${globalMaskDocument(value)}`);
				setLoadingDocument(false);
			});
	};

	useEffect(() => {
		setClientId(Rows?.clientId);
		if (Rows?.clientId) {
			handleClientSelected(Rows.clientId);
		}
	}, [Rows]);

	const handleClientSelected = async (id: string) => {
		if (!id) return;
		try {
			const clientData = await clientService.getByID(id);
			console.log("Client Data fetched:", clientData);
			if (clientData && !(clientData instanceof Error)) {
				if (clientData.tenantSolidarity === 'active') {
					console.log("Solidarity Active. Num:", clientData.tenantSolidarityNum);
					setHasSolidarity(true);
					// Default to 1 if active but no number provided, to ensure button appears
					setSolidarityLimit(clientData.tenantSolidarityNum || 1);
				} else {
					console.log("Solidarity Not Active");
					setHasSolidarity(false);
					setSolidarityLimit(0);
					setIsSolidarityEnabled(false);
					setSolidaryTenants([]);
				}

				// Setup Fee Logic
				if (clientData.tax_setup_enable) {
					setSetupFeeActive(true);
					setSetupFeeLimitMin(parseFloat(String(clientData.tax_setup_limit_min || 0)));
					setSetupFeeLimitMax(parseFloat(String(clientData.tax_setup_limit_max || 0)));
					// Set default value if available
					if (clientData.tax_setup_limit_default) {
						formRef.current?.setFieldValue("setup_amount", clientData.tax_setup_limit_default);
					}
					// Default to active if enabled for client
					setIsSendClickSetup(true);
					formRef.current?.setFieldValue("setup_tax", "true");
				} else {
					setSetupFeeActive(false);
					setIsSendClickSetup(false);
					formRef.current?.setFieldValue("setup_tax", "false");
					formRef.current?.setFieldValue("setup_amount", "0,00");
				}
			}
		} catch (error) {
			console.error("Erro ao buscar dados do cliente", error);
			setHasSolidarity(false);
			setSetupFeeActive(false);
		}
	};

	return (
		<>
			<LoadingDialog isLoading={isLoadingDocument} loadingText={`Localizando documento...`} />
			<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
				<Grid container direction="column" padding={2} spacing={2}>
					<Grid item xs={11}>
						<Typography variant="h6" color={globalColorTytle}>
							Dados do Inquilino
						</Typography>
					</Grid>
					{/* Campo de autocomplete e aviso */}
					<Grid container item direction="row" spacing={2} marginBottom={2}>
						<Grid item xs={12} sm={12} md={4}>
							<LocalAutoCompleteClient
								isExternalLoading={isLoading}
								namecampo="clientId"
								labelSelect="Corretor / Imobiliaria"
								clientDefault={clientId}
								idSelected={handleClientSelected}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={8}>
							<Alert severity="info" sx={{ borderRadius: 3 }}>
								Atenção: É obrigatório o preenchimento do e-mail e celular do locatário. O preenchimento do e-mail e/ou celular que não
								seja do locatário poderá ocasionar em recusa ou em não contratação da garantia.
							</Alert>
						</Grid>

						{/* Campos principais do formulário */}
						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Tipo de Cliente" name="typePerson" disabled select onChange={(e) => setClientType(e.target.value)}>
									<MenuItem value="physical">Física</MenuItem>
									<MenuItem value="legal">Jurídica</MenuItem>
								</VTextField>
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VMaskTextField
									fullWidth
									label={clientType === "physical" ? "CPF" : "Documento"}
									name="document"
									disabled={id !== "novo" || isLoading}
									mask={clientType === "physical" ? "cpf" : "cnpj"}
									value={cpfValue}
									onChange={(e) => setCpfValue(e.target.value)}
									onBlur={(e) => handleCPF(e.target.value)}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Nome" name="name" disabled />
							</Grid>
						</Grid>

						{/* Campos adicionais */}
						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Nome da Mãe" name="motherName" disabled={isLoading} />
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Data de Nascimento" name="birthDate" disabled type="date" />
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField
									fullWidth
									label="Email"
									name="email"
									disabled={isLoading}
									trimEmptySpaces
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton aria-label="toggle email visibility" edge="end">
													<Email />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>

						{/* Campos de relação de trabalho, sexo e estado civil */}
						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Relação de Trabalho" name="employmentRelationShip" disabled={isLoading} select>
									<MenuItem value="retired">Aposentado</MenuItem>
									<MenuItem value="freelance">Autônomo</MenuItem>
									<MenuItem value="entrepreneur">Empresário</MenuItem>
									<MenuItem value="student">Estudante</MenuItem>
									<MenuItem value="civil_servant">Funcionário Público</MenuItem>
									<MenuItem value="private_servant">Funcionário CLT</MenuItem>
									<MenuItem value="liberal">Liberal</MenuItem>
									<MenuItem value="rent_income">Renda Aluguéis</MenuItem>
								</VTextField>
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Sexo" name="sex" disabled={isLoading} select>
									<MenuItem value="male">Masculino</MenuItem>
									<MenuItem value="female">Feminino</MenuItem>
								</VTextField>
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Estado Civil" name="maritalStatus" disabled={isLoading} select>
									<MenuItem value="single">Solteiro</MenuItem>
									<MenuItem value="married">Casado</MenuItem>
									<MenuItem value="widower">Viúvo</MenuItem>
									<MenuItem value="divorced">Divorciado</MenuItem>
									<MenuItem value="stable_union">União Estável</MenuItem>
								</VTextField>
							</Grid>
						</Grid>

						{/* Campos de telefone e celular */}
						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VMaskTextField fullWidth label="Celular" name="phone" disabled={isLoading} mask="cellphone" />
							</Grid>
							<Grid item xs={12} md={4}>
								<VMaskTextField fullWidth label="Telefone" name="mobilePhone" disabled={isLoading} mask="cell" />
							</Grid>
						</Grid>

						{/* Configurações adicionais para perfil com permissão */}
						{(permissionLevel === 1 && permissions?.analysis_options?.disableSerasa) && (
							<Grid container item direction="row" spacing={2} >
								<Grid item xs={12} md={2}>
									<FormControlLabel
										control={<Switch onChange={handleTipping} name="isTipping" color="secondary" checked={isTippingChecked} />}
										label="Tombamento"
									/>
								</Grid>
								<Grid item xs={12} md={2}>
									<FormControlLabel
										control={<Switch onChange={handleChangeSerasa} name="sendSerasa" color="primary" checked={sendSerasaChecked} />}
										label="Enviar Serasa"
									/>
								</Grid>
								<Grid item xs={12} md={8}>
									<VTextField fullWidth label="Motivo não envio para Serasa" name="serasaResponse" disabled={enableReason} />
								</Grid>
							</Grid>
						)}

						{/* Taxa de Setup */}
						{setupFeeActive && checkByProfileGuard(PermissionRolesEnum.AnalysisOptions, PermissionActionsEnum.SetupFee) && (
							<Grid item xs={12}>
								<Card variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 3 }}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={3}>
											<FormControlLabel
												control={
													<Switch
														checked={isSendClickSetup}
														onChange={handleChangeClickSetup}
														name="setup_tax_switch"
														color="primary"
													/>
												}
												label="Cobrar taxa de setup"
											/>
										</Grid>
										<Grid item xs={12} md={3}>
											<VMaskTextField
												fullWidth
												label="Valor Taxa de Setup"
												name="setup_amount"
												disabled={!isSendClickSetup}
												mask="real"
												onBlur={(e) => handleCheckSetupValue(e.target.value)}
											/>
										</Grid>
										<Grid item>
											<HtmlTooltip title={`O valor da taxa de setup deverá estar entre R$ ${globalMaskReal(setupFeeLimitMin.toFixed(2))} e R$ ${globalMaskReal(setupFeeLimitMax.toFixed(2))}`}>
												<IconButton color="primary">
													<HelpOutlineOutlined />
												</IconButton>
											</HtmlTooltip>
										</Grid>
										{showSetupFeeAlert && (
											<Grid item xs={12} md={5}>
												<Alert severity="warning" sx={{ borderRadius: 3 }}>
													O valor da taxa de setup deverá estar entre R$ {globalMaskReal(setupFeeLimitMin.toFixed(2))} e R$ {globalMaskReal(setupFeeLimitMax.toFixed(2))}
												</Alert>
											</Grid>
										)}
									</Grid>
								</Card>
							</Grid>
						)}

						{/* Locatário Solidário */}
						{hasSolidarity && permissions?.analysis_options?.solidaryTenant && (
							<Grid container item direction="column" spacing={2} sx={{ mt: 1, p: 1, ml: 1 }}	 >
								{solidaryTenants.map((tenant, index) => (
									<TenantSolidarityFields
										key={tenant.id || index}
										index={index}
										onRemove={handleRemoveSolidaryTenant}
										isLoading={isLoading}
										formRef={formRef}
									/>
								))}

								{solidaryTenants.length < solidarityLimit && (
									<Grid item>
										<Button variant="outlined" onClick={handleAddSolidaryTenant}>
											Adicionar Locatário Solidário
										</Button>
									</Grid>
								)}
							</Grid>
						)}
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default TenantDataSection;
